import { GenericTable } from '@ui-components/Table/GenericTable'
import React, { useMemo, useState } from 'react'
import { Maintenance, maintenanceColumns, Outages } from './OutagesData';
import { useAppSelector } from '@redux/hooks';
import { getFailoverHistoryAPI, getMonitoringAPI } from '../requests/request';
import { useAxiosAuth0 } from '@shared/config/useAxiosAuth0';
import { GenericFilter } from '@ui-components/Filter/GenericFilter';
import { useCachedFetch } from '@hooks/useCachedFetch';
import { ListPageSkeleton, RefreshingIndicator } from '@shared/components/ListLoading';
import SegmentedControl from '@ui-components/SegmentedContol/GenericSegmentedContol';
import GenericCard from '@ui-components/Card/GenericCard';
import { useLocation, useNavigate } from 'react-router-dom';
import GenericModal from '@ui-components/Modal/GenericModal';
import { DurationBadge } from 'kontrolEdge/helpers/DurationBadge';

/** An outage as the tab renders it: the duration already formatted ("5 min.", "on-going"). */
type OutageRow = Omit<Outages, 'outageDuration'> & { outageDuration: string };

/** Both tabs come from one load, so they are cached together. */
interface OutagesAndMaintenance {
  outages: OutageRow[];
  maintenance: Maintenance[];
}

const NOTHING_RECORDED: OutagesAndMaintenance = { outages: [], maintenance: [] };

export default function OutagesView() {
  const api = useAxiosAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOutage, setSelectedOutage] = useState<OutageRow | null>(null);

  const { tenantId } = useAppSelector(state => state.selection);
  const [searchColumn, setSearchColumn] = useState("All");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const initialSelectedView = location.state?.selectedView ?? "outages";
  const [selectedView, setSelectedView] = useState<"outages" | "maintenance">(initialSelectedView);
  const itemToRender = [
    { label: "Outages", value: "outages" },
    { label: "Maintenance", value: "maintenance" },
  ];

  // Cached per tenant: coming back to the screen paints the last answer at once and
  // refreshes it underneath. A failed load shows no rows, as it always has — the tabs'
  // own "nothing found" states, not an error page.
  const {
    data: { outages: outagesList, maintenance: maintenanceList } = NOTHING_RECORDED,
    isLoading,
    isRefreshing,
  } = useCachedFetch<OutagesAndMaintenance>(
    tenantId ? `kontroledge/outages?tenantId=${tenantId}` : null,
    async () => {
      const [failoverResp, maintenanceResp] = await Promise.all([
        getFailoverHistoryAPI(api, tenantId),
        getMonitoringAPI(api, tenantId),
      ]);
      const failoverHistory = failoverResp?.data?.data?.failoverHistory;
      const maintenanceRows = maintenanceResp?.data?.data;
      return {
        outages: Array.isArray(failoverHistory)
          ? failoverHistory.map((p: Outages) => ({
              date: p.date,
              site: p.site ?? '–',
              description: p.description,
              outageDuration: formatDuration(p.outageDuration),
              eventId: p.eventId,
              summary: p.summary ?? '',
              detail: p.detail ?? '',
              incidentId: p.incidentId ?? '',
            }))
          : [],
        maintenance: Array.isArray(maintenanceRows)
          ? maintenanceRows.map((p: Outages) => ({
              date: p.date,
              description: p.description,
              outageDuration: formatDuration(p.outageDuration),
              eventId: p.eventId,
            }))
          : [],
      };
    }
  );

  function formatDuration(duration: number | null | undefined): string {
    // `null` from the API means the outage is still active — the
    // matching router_reachable hasn't arrived yet (or the row was
    // hand-curated without a duration). Render a placeholder rather
    // than "0 sec." so operators can tell ongoing vs resolved at a glance.
    if (duration == null) {
      return "on-going";
    }
    if (duration < 60) {
      return `${duration} sec.`;
    }
    const minutes = duration / 60;
    if (minutes < 60) {
      return `${Math.round(minutes)} min.`;
    }
    const hours = minutes / 60;
    return `${Math.round(hours)} hr${Math.round(hours) > 1 ? "s" : ""}.`;
  }

  const filteredData = useMemo(() => {
    // Coerce to `Array<OutageRow | Maintenance>` (a single array of a
    // union) instead of `OutageRow[] | Maintenance[]` (a union of arrays).
    // TS can't pick a common `.filter` signature for the latter when the
    // member shapes diverge (outages carry site, summary, incident id).
    const list: Array<OutageRow | Maintenance> =
      selectedView === "outages" ? outagesList : maintenanceList;
    return list.filter((item) => {
      if (searchColumn === "All") {
        const term = searchTerm.toLowerCase();
        return (
          item.date.toLowerCase().includes(term) ||
          item.description.toLowerCase().includes(term) ||
          (('site' in item) && (item as OutageRow).site?.toLowerCase().includes(term)) ||
          (('incidentId' in item) && (item as OutageRow).incidentId?.toLowerCase().includes(term))
        );
      }
      switch (searchColumn) {
        case "Date":
          return item.date.toLowerCase().includes(searchTerm.toLowerCase());
        case "Description":
          return item.description.toLowerCase().includes(searchTerm.toLowerCase());
        case "Site":
          return ('site' in item) && (item as OutageRow).site?.toLowerCase().includes(searchTerm.toLowerCase());
        default:
          return true;
      }
    });
  }, [selectedView, outagesList, maintenanceList, searchColumn, searchTerm]);

  // Nothing to show yet: the page's shape in skeleton, not a screen-wide overlay.
  if (isLoading) {
    return <ListPageSkeleton columns={5} />;
  }

  return (
    <div className="relative">
        <RefreshingIndicator active={isRefreshing} />
        <GenericCard
          headerContent={
          <SegmentedControl
            options={itemToRender}
            value={selectedView}
            onChange={(value) => setSelectedView(value as "outages" | "maintenance")}
            size="small"
          />}
          isCollapsible={false}
        >
        <div className="flex justify-end mb-5">
          <GenericFilter
            searchColumn={searchColumn}
            onSearchColumnChange={setSearchColumn}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            columnOptions={
              selectedView === "outages"
                ? ["All", "Description", "Date", "Site"]
                : ["All", "Description", "Date"]
            }
          />
        </div>
        {selectedView === "outages" ? (
          <div>
            {(filteredData as OutageRow[]).length === 0 ? (
              <p className="text-sm text-semantic-text-secondary text-center py-6">
                No outages found
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-2 text-xs font-medium text-semantic-text-secondary">ID</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-semantic-text-secondary">Date</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-semantic-text-secondary">Site</th>
                    <th className="text-left py-2 px-2 text-xs font-medium text-semantic-text-secondary">Duration</th>
                    <th className="py-2 px-2" />
                  </tr>
                </thead>
                <tbody>
                  {(filteredData as OutageRow[]).map((row, idx) => (
                    <React.Fragment key={idx}>
                      <tr className="border-t border-gray-100">
                        <td className="py-2 px-2 text-xs text-semantic-text-secondary whitespace-nowrap">
                          {row.incidentId || "–"}
                        </td>
                        <td className="py-2 px-2 text-semantic-text-primary whitespace-nowrap">
                          {row.date}
                        </td>
                        <td className="py-2 px-2">
                          {row.site && row.site !== '–' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-semantic-text-primary">
                              {row.site}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-semantic-text-primary">
                          <DurationBadge value={row.outageDuration} />
                        </td>
                        <td className="py-2 pl-2 pr-0">
                          {row.detail && (
                            <button
                              className="text-xs text-semantic-text-secondary hover:text-semantic-text-primary underline"
                              onClick={() => {
                                setSelectedOutage(row);
                                setIsModalOpen(true);
                              }}
                            >
                              Details
                            </button>
                          )}
                        </td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td colSpan={5} className="px-2 pb-2">
                          <div className="max-h-10 overflow-y-auto text-xs text-semantic-text-secondary">
                            {row.description}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <GenericTable
            columns={maintenanceColumns}
            data={filteredData as Maintenance[]}
            initialRowsPerPage={5}
            rowsPerPageOptions={[5, 10]}
            width="100%"
            tableClassName="bg-semantic-bg-primary"
          />
        )}
        </GenericCard>

        <GenericModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedOutage(null);
          }}
          title="Outage Details"
          size="lg"
        >
          {selectedOutage && (
            <div className="space-y-4 p-4">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-semantic-text-secondary">
                {selectedOutage.incidentId && (
                  <span><strong>ID:</strong> {selectedOutage.incidentId}</span>
                )}
                <span><strong>Date:</strong> {selectedOutage.date}</span>
                {selectedOutage.site && selectedOutage.site !== '–' && (
                  <span><strong>Site:</strong> {selectedOutage.site}</span>
                )}
                <span><strong>Duration:</strong> {selectedOutage.outageDuration}</span>
              </div>

              {selectedOutage.detail && (
                <div>
                  <p className="text-sm text-semantic-text-secondary whitespace-pre-wrap text-justify">
                    {selectedOutage.detail}
                  </p>
                </div>
              )}

              <div className="pt-3 border-t border-gray-200">
                <p className="text-xs text-semantic-text-secondary">
                  For more information, open a{" "}
                  <a
                    href="/support/request"
                    className="underline hover:text-semantic-text-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsModalOpen(false);
                      setSelectedOutage(null);
                      navigate("/support/request");
                    }}
                  >
                    help request
                  </a>
                  .
                </p>
              </div>
            </div>
          )}
        </GenericModal>
    </div>
  )
}
